import { Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import Title from 'components/Title';
import { screensharePreviewToggled } from 'features/layout/features/config/configSlice';
import { localScreenshareStopped } from 'features/screenshare/actions';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store/hooks';

const FloatingBar = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'rgba(0, 0, 0, 0.75)',
  position: 'absolute',
  borderRadius: '8px',
  padding: '8px 16px',
  right: `${theme.room.tileGap}px`,
  bottom: `${theme.room.tileGap}px`,
  opacity: 0,
  maxWidth: '75%',
}));

const WhiteButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.white,
  borderColor: theme.palette.common.white,
}));

interface LocalScreensharePreviewOverlayProps {
  floating?: boolean;
  position?: 'center' | 'left' | 'right';
  wrap?: boolean;
}

const LocalScreensharePreviewOverlay = ({
  position = 'right',
  floating,
  wrap,
}: LocalScreensharePreviewOverlayProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const togglePreview = () => dispatch(screensharePreviewToggled());
  const stopScrenshare = () => dispatch(localScreenshareStopped());

  const renderControls = () => (
    <Box>
      <Button
        size={floating ? 'small' : 'medium'}
        type="button"
        sx={[
          floating
            ? {
                fontWeight: 'bold',
              }
            : {
                fontWeight: 'normal',
              },
        ]}
        variant={floating ? 'text' : 'contained'}
        onClick={stopScrenshare}
      >
        {t('room:screenshare.stop_sharing')}
      </Button>
      <WhiteButton
        size={floating ? 'small' : 'medium'}
        type="button"
        sx={[
          {
            ml: 4,
          },
          floating
            ? {
                fontWeight: 'bold',
              }
            : {
                fontWeight: 'normal',
              },
        ]}
        variant={floating ? 'text' : 'outlined'}
        onClick={togglePreview}
        color="grayscale"
      >
        {floating
          ? t('room:screenshare.hide_preview_action')
          : t('room:screenshare.show_preview_action')}
      </WhiteButton>
    </Box>
  );

  return floating ? (
    <FloatingBar
      className="hover-child"
      sx={[
        {
          zIndex: 99999,
        },
        wrap
          ? {
              flexDirection: 'column',
            }
          : {
              flexDirection: 'row',
            },
        position === 'center'
          ? {
              maxWidth: '100%',
              width: 'max-content',
              right: '50%',
              transform: 'translateX(50%)',
            }
          : null,
        position === 'left'
          ? {
              maxWidth: 'calc(100% - 16px)',
              width: 'max-content',
              left: 4,
            }
          : null,
      ]}
    >
      <Title
        sx={[
          {
            color: '#fff',
          },
          wrap
            ? {
                mr: 0,
              }
            : {
                mr: 8,
              },
          wrap
            ? {
                mb: 2,
              }
            : {
                mb: 0,
              },
          wrap
            ? {
                textAlign: 'center',
              }
            : {
                textAlign: 'left',
              },
        ]}
        style={{
          fontSize: '14px',
        }}
      >
        {t('room:screenshare.preview_label')}
      </Title>
      {renderControls()}
    </FloatingBar>
  ) : (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        p: 4,
      }}
    >
      <Title
        sx={{
          color: '#fff',
          mb: 4,
          textAlign: 'center',
        }}
        style={{
          fontSize: '18px',
        }}
      >
        {t('room:screenshare.preview_label')}
      </Title>
      {renderControls()}
    </Box>
  );
};

export default LocalScreensharePreviewOverlay;
